// ============================================
// VoiceCommandButton — Floating mic button + 
// confirmation overlay for AI voice commands.
// Works on both desktop and mobile.
// ============================================

import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useData } from '../data/DataContext';
import { useAuth } from '../data/AuthContext';
import { useVoiceCommand } from '../hooks/useVoiceCommand';
import { cn } from './ui/utils';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, X, Check, Loader2, AlertCircle, Sparkles, Keyboard } from 'lucide-react';
import type { VoiceCommandResult } from '../data/api';
import { calculatePriority, isOwnedByUser } from '../data/mockData';
import * as api from '../data/api';
import { toast } from 'sonner';

// localStorage key for onboarding
const ONBOARDING_KEY = 'stageflow-voice-shortcut-seen';

// ============================================
// Human-readable descriptions of parsed actions
// ============================================

const NAV_MAP: Record<string, string> = {
  dashboard: '/',
  projects: '/projects',
  calendar: '/calendar',
  content: '/content',
  volunteers: '/volunteers',
  merch: '/merch',
  venues: '/venues',
  team: '/team',
  integrations: '/integrations',
  templates: '/templates',
  focus: '/focus',
  tv: '/tv',
};

function describeAction(result: VoiceCommandResult): { icon: string; title: string; detail: string } {
  if (!result.action) return { icon: '\u2753', title: 'Unknown', detail: '' };
  const p = result.params || {};

  switch (result.action) {
    case 'create_deliverable':
      return {
        icon: '\u2795',
        title: 'Create Deliverable',
        detail: [
          `"${p.name}"`,
          p.projectName && `in ${p.projectName}`,
          p.department && `\u2022 ${p.department}`,
          p.assignTo && `\u2022 assign to ${p.assignTo}`,
          p.dueDate && `\u2022 due ${p.dueDate}`,
        ].filter(Boolean).join(' '),
      };
    case 'update_status':
      return {
        icon: '\u{1F504}',
        title: 'Update Status',
        detail: `"${p.deliverableName}" \u2192 ${(p.newStatus || '').replace('-', ' ')}`,
      };
    case 'start_focus_mode':
      return { icon: '\u{1F3AF}', title: 'Start Focus Mode', detail: 'Enter focus mode to work on top priorities' };
    case 'whats_next':
      return { icon: '\u{1F4CB}', title: "What's Next?", detail: 'Show your highest priority task' };
    case 'navigate':
      return { icon: '\u{1F9ED}', title: 'Navigate', detail: `Go to ${p.destination || 'page'}` };
    case 'focus_control': {
      const actions: Record<string, string> = {
        pause: '\u23F8\uFE0F Pause timer',
        resume: '\u25B6\uFE0F Resume timer',
        skip: '\u23ED\uFE0F Skip to next task',
        complete: '\u2705 Mark task as done',
        exit: '\u{1F6AA} Exit focus mode',
      };
      return { icon: '\u{1F3AE}', title: 'Focus Control', detail: actions[p.action] || p.action };
    }
    case 'assign_task':
      return { icon: '\u{1F465}', title: 'Assign Task', detail: `"${p.deliverableName}" \u2192 ${p.personName}` };
    case 'time_query':
      return { icon: '\u23F1\uFE0F', title: 'Time Query', detail: p.deliverableName ? `How much time on "${p.deliverableName}"` : 'Total focus time' };
    case 'plan_deliverable':
      return {
        icon: '\u2728',
        title: 'Plan with AI',
        detail: `"${p.request}" \u2014 you'll review the steps, assignee, and schedule before anything is created`,
      };
    case 'update_deliverable':
      return {
        icon: '\u2728',
        title: 'Update with AI',
        detail: `Update "${p.deliverableName}": ${p.request} \u2014 you'll review before anything is saved`,
      };
    default:
      return { icon: '\u2753', title: result.action, detail: JSON.stringify(p) };
  }
}

// ============================================
// Props
// ============================================

interface VoiceCommandButtonProps {
  /** Mobile-optimized layout */
  mobile?: boolean;
  /** Focus mode callbacks */
  onFocusControl?: (action: 'pause' | 'resume' | 'skip' | 'complete' | 'exit') => void;
  /** Whether currently in focus mode */
  inFocusMode?: boolean;
  /** Current focus task name */
  currentFocusTask?: string;
  /** Custom class for the button */
  className?: string;
}

// ============================================
// Component
// ============================================

export function VoiceCommandButton({
  mobile = false,
  onFocusControl,
  inFocusMode = false,
  currentFocusTask,
  className,
}: VoiceCommandButtonProps) {
  const navigate = useNavigate();
  const { projects, deliverables, teamMembers, createDeliverable, updateDeliverable } = useData();
  const { currentUser } = useAuth();

  const {
    state,
    transcript,
    interimTranscript,
    result,
    error,
    isSupported,
    startListening,
    stopListening,
    confirm,
    cancel,
    setFocusModeContext,
  } = useVoiceCommand();

  // Track whether Space is held for push-to-talk
  const spaceHeldRef = useRef(false);
  const [showShortcutHint, setShowShortcutHint] = useState(false);

  // ---- Desktop push-to-talk: hold Space ----
  useEffect(() => {
    if (mobile || !isSupported) return;

    const isEditableTarget = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((e.target as HTMLElement)?.isContentEditable) return true;
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (isEditableTarget(e)) return;
      // Only start if idle / error / success
      if (state !== 'idle' && state !== 'error' && state !== 'success') return;

      e.preventDefault();
      spaceHeldRef.current = true;
      startListening();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (!spaceHeldRef.current) return;

      e.preventDefault();
      spaceHeldRef.current = false;
      if (state === 'listening') {
        stopListening();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      spaceHeldRef.current = false;
    };
  }, [mobile, isSupported, state, startListening, stopListening]);

  // ---- One-time onboarding toast for Space shortcut ----
  useEffect(() => {
    if (mobile || !isSupported) return;
    // Only show once ever, after a short delay so the page settles
    try {
      if (localStorage.getItem(ONBOARDING_KEY)) return;
    } catch { return; }

    const timer = setTimeout(() => {
      toast('Voice commands are ready!', {
        description: 'Hold Space anywhere to talk. Try "Add a deliverable" or "What\'s next?"',
        duration: 8000,
        icon: <Mic className="w-4 h-4 text-indigo-500" />,
        action: {
          label: 'Got it',
          onClick: () => {},
        },
      });
      try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch {}
    }, 2500);

    return () => clearTimeout(timer);
  }, [mobile, isSupported]);

  // Keep focus mode context in sync
  useEffect(() => {
    setFocusModeContext(inFocusMode, currentFocusTask);
  }, [inFocusMode, currentFocusTask, setFocusModeContext]);

  // ---- Action executor ----
  const executeAction = useCallback(async (res: VoiceCommandResult) => {
    if (!res.action) return;
    const p = res.params || {};

    try {
      switch (res.action) {
        case 'create_deliverable': {
          // Find matching project
          const matchProject = projects.find(pr =>
            pr.name.toLowerCase().includes((p.projectName || '').toLowerCase()) ||
            (p.projectName || '').toLowerCase().includes(pr.name.toLowerCase())
          );

          const newDel: any = {
            id: `del-voice-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
            name: p.name,
            projectId: matchProject?.id || projects[0]?.id || '',
            department: p.department || 'Marketing',
            energyLevel: p.energyLevel || 'medium-effort',
            status: 'not-started',
            task: p.task || '',
            workingTeam: p.assignTo ? [p.assignTo] : (currentUser?.name ? [currentUser.name] : []),
            dueDate: p.dueDate || matchProject?.dueDate || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
            kickoffDate: new Date().toISOString().split('T')[0],
          };

          await createDeliverable(newDel);
          break;
        }

        case 'update_status': {
          const match = deliverables.find(d =>
            d.name.toLowerCase().includes((p.deliverableName || '').toLowerCase()) ||
            (p.deliverableName || '').toLowerCase().includes(d.name.toLowerCase())
          );
          if (match) {
            await updateDeliverable(match.id, { ...match, status: p.newStatus });
          }
          break;
        }

        case 'start_focus_mode':
          navigate('/focus');
          break;

        case 'plan_deliverable':
          // No direct creation — the Plan with AI review screen is the
          // confirmation surface; it carries the request via location state.
          navigate('/deliverables/plan', { state: { requestText: p.request || res.transcript } });
          break;

        case 'update_deliverable': {
          // Fuzzy-match the named deliverable, then open the same review screen
          // in update mode with the change request pre-filled.
          const target = deliverables.find(d =>
            d.name.toLowerCase().includes((p.deliverableName || '').toLowerCase()) ||
            (p.deliverableName || '').toLowerCase().includes(d.name.toLowerCase())
          );
          if (target) {
            navigate('/deliverables/plan', { state: { updateDeliverableId: target.id, requestText: p.request || '' } });
          } else {
            navigate('/deliverables');
          }
          break;
        }

        case 'navigate':
          if (p.destination && NAV_MAP[p.destination]) {
            navigate(NAV_MAP[p.destination]);
          }
          break;

        case 'focus_control':
          if (onFocusControl && p.action) {
            onFocusControl(p.action);
          }
          break;

        case 'assign_task': {
          const match = deliverables.find(d =>
            d.name.toLowerCase().includes((p.deliverableName || '').toLowerCase()) ||
            (p.deliverableName || '').toLowerCase().includes(d.name.toLowerCase())
          );
          const teamMatch = teamMembers.find(t =>
            t.name.toLowerCase().includes((p.personName || '').toLowerCase()) ||
            (p.personName || '').toLowerCase().includes(t.name.toLowerCase())
          );
          if (match && teamMatch) {
            const newTeam = [...new Set([...match.workingTeam, teamMatch.name])];
            await updateDeliverable(match.id, { ...match, workingTeam: newTeam });
          }
          break;
        }

        case 'whats_next': {
          const ranked = calculatePriority(deliverables, projects);
          const top = currentUser
            ? ranked.find(t => isOwnedByUser(t.deliverable, currentUser.name))
            : ranked[0];
          if (top) {
            if ('speechSynthesis' in window) {
              const utterance = new SpeechSynthesisUtterance(
                `Your top priority is: ${top.deliverable.name}, for ${top.project.name}. ${top.reason}.`
              );
              utterance.rate = 0.95;
              speechSynthesis.speak(utterance);
            }
          }
          break;
        }

        case 'time_query': {
          try {
            const sessions = await api.fetchCollection<any>('focus-sessions');
            let totalSec: number;
            let label: string;

            if (p.deliverableName) {
              const match = deliverables.find(d =>
                d.name.toLowerCase().includes((p.deliverableName || '').toLowerCase())
              );
              totalSec = sessions
                .filter((s: any) => match ? s.deliverableId === match.id : s.deliverableName?.toLowerCase().includes((p.deliverableName || '').toLowerCase()))
                .reduce((a: number, s: any) => a + (s.durationSeconds || 0), 0);
              label = match?.name || p.deliverableName;
            } else {
              totalSec = sessions.reduce((a: number, s: any) => a + (s.durationSeconds || 0), 0);
              label = 'all tasks';
            }

            const h = Math.floor(totalSec / 3600);
            const m = Math.floor((totalSec % 3600) / 60);
            const timeStr = h > 0 ? `${h} hours and ${m} minutes` : `${m} minutes`;

            if ('speechSynthesis' in window) {
              const utterance = new SpeechSynthesisUtterance(
                `You've logged ${timeStr} on ${label}.`
              );
              utterance.rate = 0.95;
              speechSynthesis.speak(utterance);
            }
          } catch (err) {
            console.error('Time query error:', err);
          }
          break;
        }
      }
    } catch (err) {
      console.error('Voice command execution error:', err);
    }
  }, [projects, deliverables, teamMembers, currentUser, navigate, createDeliverable, updateDeliverable, onFocusControl]);

  // Handle confirm
  const handleConfirm = useCallback(async () => {
    if (!result) return;
    confirm();
    await executeAction(result);
    // Brief success state, then reset
    setTimeout(() => cancel(), 1500);
  }, [result, confirm, executeAction, cancel]);

  // Haptic feedback
  const vibrate = (ms: number) => {
    if (navigator.vibrate) navigator.vibrate(ms);
  };

  if (!isSupported) return null;

  const actionDesc = result ? describeAction(result) : null;
  const isActive = state !== 'idle';

  // ============================================
  // Render
  // ============================================

  return (
    <>
      {/* ---- Floating Mic Button ---- */}
      <motion.button
        onClick={() => {
          if (state === 'listening') {
            stopListening();
          } else if (state === 'idle' || state === 'error' || state === 'success') {
            vibrate(50);
            startListening();
          }
        }}
        onMouseEnter={() => !mobile && setShowShortcutHint(true)}
        onMouseLeave={() => setShowShortcutHint(false)}
        disabled={state === 'processing' || state === 'confirming' || state === 'executing'}
        className={cn(
          'relative z-30 flex items-center justify-center rounded-full shadow-2xl transition-all active:scale-95',
          mobile ? 'w-16 h-16' : 'w-14 h-14',
          state === 'listening'
            ? 'bg-red-500 shadow-red-500/30'
            : state === 'processing'
            ? 'bg-indigo-600 shadow-indigo-500/30'
            : state === 'error'
            ? 'bg-red-500/60 shadow-red-500/20'
            : state === 'success'
            ? 'bg-emerald-500 shadow-emerald-500/30'
            : 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-indigo-500/30',
          className,
        )}
        whileTap={{ scale: 0.92 }}
      >
        {state === 'listening' ? (
          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <Mic className={cn('text-white', mobile ? 'w-7 h-7' : 'w-6 h-6')} />
          </motion.div>
        ) : state === 'processing' ? (
          <Loader2 className={cn('text-white animate-spin', mobile ? 'w-7 h-7' : 'w-6 h-6')} />
        ) : state === 'success' ? (
          <Check className={cn('text-white', mobile ? 'w-7 h-7' : 'w-6 h-6')} />
        ) : state === 'error' ? (
          <AlertCircle className={cn('text-white', mobile ? 'w-7 h-7' : 'w-6 h-6')} />
        ) : (
          <Mic className={cn('text-white', mobile ? 'w-7 h-7' : 'w-6 h-6')} />
        )}

        {/* Listening pulse ring */}
        {state === 'listening' && (
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-red-400"
            animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
          />
        )}
      </motion.button>

      {/* ---- Shortcut Hint Tooltip ---- */}
      <AnimatePresence>
        {showShortcutHint && state === 'idle' && !mobile && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="fixed right-[84px] bottom-[26px] z-50 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-medium shadow-lg whitespace-nowrap"
          >
            <Keyboard className="w-3.5 h-3.5 text-gray-400" />
            Hold
            <kbd className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/15 text-[11px] font-mono">
              Space
            </kbd>
            to talk
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Overlay Panel ---- */}
      <AnimatePresence>
        {isActive && state !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={cn(
              'fixed z-40 rounded-2xl border shadow-2xl overflow-hidden',
              mobile
                ? 'left-4 right-4 bottom-28 border-white/10 bg-[#1a1830]/95 backdrop-blur-xl'
                : 'right-6 bottom-24 w-[360px] border-gray-200 bg-white/95 backdrop-blur-xl',
            )}
          >
            {/* Header */}
            <div className={cn(
              'flex items-center justify-between px-4 py-3 border-b',
              mobile ? 'border-white/10' : 'border-gray-100',
            )}>
              <div className="flex items-center gap-2">
                <Sparkles className={cn('w-4 h-4', mobile ? 'text-amber-400' : 'text-indigo-500')} />
                <span className={cn('text-[14px] font-medium', mobile ? 'text-white/90' : 'text-gray-800')}>
                  Voice Command
                </span>
              </div>
              <button
                onClick={cancel}
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  mobile ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-100 text-gray-400',
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4">
              {/* Listening state */}
              {state === 'listening' && (
                <div className="text-center py-4">
                  <motion.div
                    className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-3',
                      mobile ? 'bg-red-500/20' : 'bg-red-50'
                    )}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className={cn('text-[13px] font-medium', mobile ? 'text-red-300' : 'text-red-600')}>
                      Listening...
                    </span>
                  </motion.div>
                  <p className={cn('text-[15px] min-h-[24px]', mobile ? 'text-white/70' : 'text-gray-600')}>
                    {interimTranscript || transcript || 'Say a command...'}
                  </p>
                  <p className={cn('text-[12px] mt-3', mobile ? 'text-indigo-300/40' : 'text-gray-400')}>
                    {spaceHeldRef.current
                      ? 'Release Space when done speaking'
                      : 'Try: "Add a deliverable for poster design" or "What\'s next?"'}
                  </p>
                </div>
              )}

              {/* Processing */}
              {state === 'processing' && (
                <div className="text-center py-4">
                  <Loader2 className={cn('w-8 h-8 mx-auto mb-3 animate-spin', mobile ? 'text-indigo-400' : 'text-indigo-500')} />
                  <p className={cn('text-[14px] mb-1', mobile ? 'text-white/70' : 'text-gray-600')}>
                    Processing...
                  </p>
                  <p className={cn('text-[13px] italic', mobile ? 'text-indigo-300/50' : 'text-gray-400')}>
                    "{transcript}"
                  </p>
                </div>
              )}

              {/* Confirmation */}
              {state === 'confirming' && actionDesc && (
                <div>
                  <p className={cn('text-[12px] mb-2', mobile ? 'text-indigo-300/50' : 'text-gray-400')}>
                    I heard: "{transcript}"
                  </p>

                  <div className={cn(
                    'rounded-xl p-4 mb-4',
                    mobile ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100',
                  )}>
                    <div className="flex items-start gap-3">
                      <span className="text-[24px] mt-0.5">{actionDesc.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-[15px] font-medium mb-1', mobile ? 'text-white/90' : 'text-gray-800')}>
                          {actionDesc.title}
                        </p>
                        <p className={cn('text-[14px] leading-relaxed', mobile ? 'text-indigo-200/70' : 'text-gray-600')}>
                          {actionDesc.detail}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => { vibrate(50); handleConfirm(); }}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-[15px] font-medium transition-all active:scale-[0.98]',
                        mobile
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 active:bg-emerald-500/30'
                          : 'bg-emerald-500 text-white hover:bg-emerald-600',
                      )}
                    >
                      <Check className="w-4 h-4" />
                      Yes, do it
                    </button>
                    <button
                      onClick={() => { vibrate(30); cancel(); }}
                      className={cn(
                        'flex-1 py-3.5 rounded-xl text-[15px] font-medium transition-all active:scale-[0.98]',
                        mobile
                          ? 'bg-white/5 text-indigo-300/70 border border-white/5 active:bg-white/10'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                      )}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Executing */}
              {state === 'executing' && actionDesc && (
                <div className="text-center py-4">
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="text-[36px] mb-2"
                  >
                    {actionDesc.icon}
                  </motion.div>
                  <p className={cn('text-[15px] font-medium', mobile ? 'text-emerald-300' : 'text-emerald-600')}>
                    Executing...
                  </p>
                </div>
              )}

              {/* Success */}
              {state === 'success' && (
                <div className="text-center py-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <Check className={cn('w-10 h-10 mx-auto mb-2', mobile ? 'text-emerald-400' : 'text-emerald-500')} />
                  </motion.div>
                  <p className={cn('text-[15px] font-medium', mobile ? 'text-emerald-300' : 'text-emerald-600')}>
                    Done!
                  </p>
                </div>
              )}

              {/* Error */}
              {state === 'error' && (
                <div className="text-center py-4">
                  <AlertCircle className={cn('w-8 h-8 mx-auto mb-2', mobile ? 'text-red-400' : 'text-red-500')} />
                  <p className={cn('text-[14px] mb-3', mobile ? 'text-red-300/80' : 'text-red-600')}>
                    {error}
                  </p>
                  <button
                    onClick={() => { vibrate(30); startListening(); }}
                    className={cn(
                      'px-5 py-2.5 rounded-xl text-[14px] font-medium transition-all active:scale-[0.98]',
                      mobile
                        ? 'bg-white/10 text-white/70 active:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                    )}
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}